import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import useInView from './useInView';
import { LANDING_FONT, LC, MOTION, easeOut, r } from './landingTokens';

// ─── Relevance ring ───────────────────────────────────────────────────────────
// Green conic-gradient ring = "how well the evidence fits you". Deliberately a
// different color AND shape from the graphite StrengthBars (Brand brief §5).
// `value` is the displayed number; `pct` is the fill (0–100, defaults value/5).
// With `animate`, the fill sweeps 0→pct and the number counts up on scroll-in
// (resets when scrolled out); rAF writes straight to the nodes — no re-renders.
const SIZES = {
  lg: { outer: 44, inner: 32, font: 12 },
  sm: { outer: 28, inner: 20, font: 10 },
};

export default function EvidenceRing({ value, pct, size = 'lg', animate = false, delay = 0, active, activeInstant, sx }) {
  const s = SIZES[size] || SIZES.lg;
  const target = pct != null ? pct : (Number(value) / 5) * 100;
  const decimals = String(value).includes('.') ? 1 : 0;
  // `active` (controlled mode) lets a parent drive the sweep from its own
  // trigger — e.g. how-it-works chains all cards off one section-level
  // observer. When undefined, the ring self-observes as usual.
  const [hostRef, hookInView, hookInstant] = useInView();
  const controlled = active !== undefined;
  const inView = controlled ? active : hookInView;
  const instant = controlled ? !!activeInstant : hookInstant;
  const numRef = useRef(null);

  useEffect(() => {
    if (!animate) return undefined;
    const host = hostRef.current;
    const num = numRef.current;
    if (!host || !num) return undefined;
    const paint = (f) => {
      host.style.background = `conic-gradient(${LC.accent} ${f * target}%, ${LC.ringTrack} 0)`;
      num.textContent = (f * Number(value)).toFixed(decimals);
    };
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { paint(1); return undefined; }
    if (!inView) { paint(0); return undefined; }
    // Entering from above (scroll-up): final state, no sweep.
    if (instant) { paint(1); return undefined; }
    let raf;
    let timer;
    const start = () => {
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min((now - t0) / MOTION.duration, 1);
        paint(easeOut(p));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };
    // `delay` lets the sweep wait for the ring's parent row to land first.
    if (delay > 0) { paint(0); timer = setTimeout(start, delay); }
    else start();
    return () => { cancelAnimationFrame(raf); clearTimeout(timer); };
  }, [animate, inView, instant, delay, target, value, decimals, hostRef]);

  return (
    <Box
      ref={animate ? hostRef : undefined}
      sx={{
        flex: 'none',
        display: 'grid',
        placeItems: 'center',
        borderRadius: '50%',
        width: r(s.outer),
        height: r(s.outer),
        background: `conic-gradient(${LC.accent} ${animate ? 0 : target}%, ${LC.ringTrack} 0)`,
        ...sx,
      }}
    >
      <Box
        ref={numRef}
        sx={{
          width: r(s.inner),
          height: r(s.inner),
          borderRadius: '50%',
          background: LC.surface,
          display: 'grid',
          placeItems: 'center',
          fontFamily: LANDING_FONT,
          fontWeight: 700,
          fontSize: r(s.font),
          color: LC.evergreen,
          letterSpacing: '-0.02em',
          fontFeatureSettings: "'tnum' 1",
        }}
      >
        {animate ? (0).toFixed(decimals) : value}
      </Box>
    </Box>
  );
}
